import { useEffect, useRef, useCallback } from 'react';
import * as PIXI from 'pixi.js';
import type { Viewport } from 'pixi-viewport';
import type { RadialMenuActionKey } from './radialMenu';
import type { RenderFurniture } from '../../../domain/types';
import type { RoomPixiRoots } from '../useRoomScene';
import type { RoomSceneState } from '../state/roomSceneState';

// ── Wall/floor (room-deco) long-press radial-menu cluster ──
// Extracted verbatim from RoomCanvas (decomposition final cleanup pass):
//  * radialMenuHandlersRef — stable ref updated every render (the update now runs
//    in this hook's body, i.e. at the exact same point of the render as before,
//    reading roomViewport through the `scene` accessor);
//  * attachSimpleLongPress — unified long-press/double-tap attach helper;
//  * the effect attaching it to the wall/floor sprites once the scene is ready.
// RoomCanvas passes `executeRadialAction` pre-wrapped in an arrow (it is declared
// later in the component body — the wrapper avoids the temporal dead zone, exactly
// as the original inline ref update did). All had this cluster as sole consumer.

export interface UseRoomDecoLongPressParams {
  isSceneReady: boolean;
  scene: RoomPixiRoots;
  previewRenderableFurniture: RenderFurniture[];
  detectRadialActionByScreenPoint: (x: number, y: number) => RadialMenuActionKey | null;
  executeRadialAction: (key: RadialMenuActionKey, f?: RenderFurniture | null) => void;
  closeRadialMenu: () => void;
  setHighlightedRadialAction: (k: RadialMenuActionKey | null) => void;
  openRoomDecoRadialMenu: (cat: 'walls' | 'floors', px: number, py: number) => void;
  sceneRef: React.MutableRefObject<RoomSceneState>;
  LONG_PRESS_THRESHOLD_MS: number;
  LONG_PRESS_MOVE_THRESHOLD: number;
}

export function useRoomDecoLongPress({
  isSceneReady,
  scene,
  previewRenderableFurniture,
  detectRadialActionByScreenPoint,
  executeRadialAction,
  closeRadialMenu,
  setHighlightedRadialAction,
  openRoomDecoRadialMenu,
  sceneRef,
  LONG_PRESS_THRESHOLD_MS,
  LONG_PRESS_MOVE_THRESHOLD,
}: UseRoomDecoLongPressParams): void {
  // Stable ref updated every render — once-attached PIXI event handlers read the latest callbacks
  // through this ref without needing to re-attach. executeRadialAction is wrapped in an arrow to
  // avoid a temporal dead zone crash (it is declared later in the component body).
  const radialMenuHandlersRef = useRef({
    roomViewport: null as Viewport | null,
    previewRenderableFurniture: [] as RenderFurniture[],
    detectRadialAction: (_x: number, _y: number) => null as RadialMenuActionKey | null,
    executeRadialAction: (_key: RadialMenuActionKey, _f?: RenderFurniture | null) => {},
    closeRadialMenu: () => {},
    setHighlightedRadialAction: (_k: RadialMenuActionKey | null) => {},
    openRoomDecoMenu: (_cat: 'walls' | 'floors', _px: number, _py: number) => {},
  });
  // Updated inline every render — no useEffect needed since we only read via ref, never render from it.
  // Intentional latest-ref: read only from PIXI pointer handlers attached once (post-commit).
  // eslint-disable-next-line react-hooks/refs -- live mirror for once-attached PIXI pointer handlers
  radialMenuHandlersRef.current = {
    roomViewport: scene.roomViewport,
    previewRenderableFurniture,
    detectRadialAction: detectRadialActionByScreenPoint,
    executeRadialAction,
    closeRadialMenu,
    setHighlightedRadialAction,
    openRoomDecoMenu: openRoomDecoRadialMenu,
  };

  /**
   * Attaches a unified long-press radial-menu interaction to any PIXI container.
   * All runtime callbacks are read from radialMenuHandlersRef so event handlers stay fresh
   * even though they are attached only once. Config closures should likewise read from
   * radialMenuHandlersRef at call time to avoid capturing stale state.
   *
   * Supports optional double-tap to open instantly (e.g. cobwebs).
   * onExecute receives the detected action key; sound/haptic for success lives there.
   */
  const attachSimpleLongPress = useCallback(
    (
      target: PIXI.Container,
      config: {
        shouldActivate?: () => boolean;
        openMenu: (px: number, py: number) => void;
        onExecute: (action: RadialMenuActionKey) => void;
        doubleTap?: boolean;
      }
    ) => {
      let lastTapAt = 0; // per-target state for double-tap detection
      target.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
        if (config.shouldActivate && !config.shouldActivate()) return;
        const clientX = (e.nativeEvent as PointerEvent).clientX ?? e.client.x;
        const clientY = (e.nativeEvent as PointerEvent).clientY ?? e.client.y;
        let lastX = clientX;
        let lastY = clientY;
        let longPressTriggered = false;
        let cleanedUp = false;

        const h = radialMenuHandlersRef.current;
        if (h.roomViewport) h.roomViewport.pause = true;

        const cleanup = () => {
          if (cleanedUp) return;
          cleanedUp = true;
          window.removeEventListener('pointermove', onMove);
          window.removeEventListener('pointerup', onUp);
        };

        const openNow = (px: number, py: number) => {
          longPressTriggered = true;
          config.openMenu(px, py);
          try { window.Telegram?.WebApp?.HapticFeedback?.impactOccurred?.('light'); } catch { /* optional */ }
        };

        let timer: number | null = null;
        if (config.doubleTap) {
          const now = Date.now();
          const isDoubleTap = now - lastTapAt < 300;
          lastTapAt = isDoubleTap ? 0 : now;
          if (isDoubleTap) {
            openNow(clientX, clientY);
          } else {
            timer = window.setTimeout(() => { openNow(lastX, lastY); }, LONG_PRESS_THRESHOLD_MS);
          }
        } else {
          timer = window.setTimeout(() => { openNow(lastX, lastY); }, LONG_PRESS_THRESHOLD_MS);
        }

        const onMove = (ev: PointerEvent) => {
          lastX = ev.clientX;
          lastY = ev.clientY;
          const hh = radialMenuHandlersRef.current;
          if (longPressTriggered) {
            hh.setHighlightedRadialAction(hh.detectRadialAction(ev.clientX, ev.clientY));
          } else {
            const dx = ev.clientX - clientX;
            const dy = ev.clientY - clientY;
            if (dx * dx + dy * dy > LONG_PRESS_MOVE_THRESHOLD * LONG_PRESS_MOVE_THRESHOLD) {
              if (timer !== null) { window.clearTimeout(timer); timer = null; }
              if (radialMenuHandlersRef.current.roomViewport) {
                radialMenuHandlersRef.current.roomViewport.pause = false;
              }
              cleanup();
            }
          }
        };

        const onUp = (ev: PointerEvent) => {
          cleanup();
          if (timer !== null) { window.clearTimeout(timer); timer = null; }
          const hh = radialMenuHandlersRef.current;
          if (hh.roomViewport) hh.roomViewport.pause = false;
          if (longPressTriggered) {
            const action = hh.detectRadialAction(ev.clientX, ev.clientY);
            if (action) config.onExecute(action);
            hh.closeRadialMenu();
          }
        };

        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
      });
    },
    // LONG_PRESS_* — module constants (stable); radialMenuHandlersRef — ref bag only.
    // Deps are constants so attachSimpleLongPress identity stays stable → wall/floor attach 1×.
    [LONG_PRESS_THRESHOLD_MS, LONG_PRESS_MOVE_THRESHOLD]
  );

  // Attach radial-menu long-press to wall/floor sprites once the scene is ready.
  // Uses attachSimpleLongPress — same logic as cobweb sprites and furniture items.
  useEffect(() => {
    if (!isSceneReady) return;

    const setupSprite = (sprite: PIXI.Sprite | null, category: 'walls' | 'floors') => {
      if (!sprite) return;
      attachSimpleLongPress(sprite, {
        shouldActivate: () => !!radialMenuHandlersRef.current.previewRenderableFurniture
          .find(f => f.furniture_catalog?.category === category),
        openMenu: (px, py) => radialMenuHandlersRef.current.openRoomDecoMenu(category, px, py),
        onExecute: (action) => {
          const h = radialMenuHandlersRef.current;
          const item = h.previewRenderableFurniture.find(f => f.furniture_catalog?.category === category);
          if (item) h.executeRadialAction(action, item);
        },
      });
    };

    setupSprite(sceneRef.current.sprites.wallLeft, 'walls');
    setupSprite(sceneRef.current.sprites.wallRight, 'walls');
    setupSprite(sceneRef.current.sprites.floor, 'floors');
    // sceneRef — PIXI bag, читається імперативно коли isSceneReady flips true.
  }, [isSceneReady, attachSimpleLongPress, sceneRef]);
}
